import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Search } from "lucide-react";
import PageHeader from "shared/PageHeader";
import Alert from "shared/Alert";
import FilterChip from "shared/FilterChip";
import PagerNav from "shared/PagerNav";
import StatusPill from "shared/StatusPill";
import Button from "shared/Button";
import type { Tone } from "shared/tones";
import { FORM_CONTROL, TABLE_SHELL, TBODY_ROW, TD, TH, THEAD_ROW } from "shared/tableStyles";
import { apiErrorMessage } from "./apiError";
import { PATH_STATES, getOutreachPaths, type OutreachPath, type OutreachPathList, type PathState } from "./api/OutreachTouchesApi";
import { daysAgo, formatCalendarDate } from "./outreachDates";
import LinkedInHubTabs from "./ui/LinkedInHubTabs";
import PathStepper from "./ui/PathStepper";
import { resultStatus } from "./ui/conversationStatus";
import PathDetailModal from "./ui/PathDetailModal";

const PAGE_SIZE = 25;

const STATE_LABEL: Record<PathState, string> = {
  not_started: "Not started", to_send: "To send", waiting: "Waiting for acceptance", accepted: "Accepted", messaged: "Messaged",
  followed_up: "Followed up", replied: "Replied", closed: "Closed", skipped: "Skipped",
};
const STATE_TONE: Record<PathState, Tone> = {
  not_started: "neutral", to_send: "info", waiting: "warning", accepted: "teal", messaged: "brand",
  followed_up: "magenta", replied: "success", closed: "neutral", skipped: "orange",
};
const ACTION_LABEL: Record<string, string> = {
  sent: "sent", accepted: "accepted", replied: "replied", no_profile: "profile not found", close: "closed", reopen: "reopened", set_contact: "contact set",
};

type SortKey = "last_action" | "days_in_state" | "company" | "rfp";

function crmBase() {
  return localStorage.getItem("authUserType") === "admin" ? "/admin/crm" : "/employee/crm";
}

function daysText(path: OutreachPath): string {
  const n = path.days_in_state;
  if (n === null) return "";
  return n === 0 ? "since today" : `for ${n} day${n === 1 ? "" : "s"}`;
}

/** The LinkedIn Pipeline: every path ever started on any RFP, one row each, with where it stands and what happened last. */
export default function LinkedInPipelinePage() {
  // All filters live in the URL. Several keys change at once (a filter plus "back to page 1"), and separate
  // per-key setters would overwrite each other, so every change goes through one `patch` write.
  const [params, setParams] = useSearchParams();
  const state = (params.get("state") ?? "") as PathState | "";
  const rfp = params.get("rfp") ?? "";
  const actor = params.get("by") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const q = params.get("q") ?? "";
  const sort = (params.get("sort") ?? "last_action") as SortKey;
  const dir = (params.get("dir") === "asc" ? "asc" : "desc") as "asc" | "desc";
  const pageN = Number.parseInt(params.get("page") ?? "1", 10);
  const page = Number.isFinite(pageN) && pageN >= 1 ? pageN : 1;
  const patch = (changes: Record<string, string | number | null>) => setParams((prev) => {
    const p = new URLSearchParams(prev);
    for (const [k, v] of Object.entries(changes)) { if (v === null || v === "") p.delete(k); else p.set(k, String(v)); }
    return p;
  }, { replace: true });
  const [typed, setTyped] = useState(q);
  const [data, setData] = useState<OutreachPathList | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState<OutreachPath | null>(null);
  const latest = useRef(0);

  // the box follows the URL when it changes from outside (browser back/forward); a debounced write leaves them equal already
  useEffect(() => { setTyped(q); }, [q]);

  // the search box updates the URL after a short pause, not on every key
  useEffect(() => {
    const t = setTimeout(() => { if (typed !== q) patch({ q: typed, page: null }); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, q]);

  useEffect(() => {
    const mine = ++latest.current;      // a slower, older answer must never overwrite a newer one
    setLoading(true);
    getOutreachPaths({
      opportunityId: rfp ? Number(rfp) : undefined, state: state || undefined, search: q, actor: actor || undefined,
      fromDate: from || undefined, toDate: to || undefined, sort, direction: dir, page, pageSize: PAGE_SIZE,
    })
      .then((d) => { if (mine === latest.current) { setData(d); setError(""); } })
      .catch((e) => { if (mine === latest.current) setError(apiErrorMessage(e, "Could not load the pipeline.")); })
      .finally(() => { if (mine === latest.current) setLoading(false); });
  }, [rfp, state, actor, from, to, sort, dir, page, q]);

  const filtered = !!(state || rfp || actor || from || to || q);
  const reset = () => { setTyped(""); setParams({}, { replace: true }); };
  const narrow = (key: string) => (v: string) => patch({ [key]: v, page: null });      // a new filter starts at the first page
  const setPage = (n: number) => patch({ page: n <= 1 ? null : n });
  const sortBy = (key: SortKey) => {
    const nextDir = sort === key ? (dir === "asc" ? "desc" : "asc") : (key === "company" || key === "rfp" ? "asc" : "desc");
    patch({ sort: key === "last_action" ? null : key, dir: nextDir === "desc" ? null : nextDir, page: null });
  };
  const sortHead = (key: SortKey, label: string) => (
    <th className={TH} aria-sort={sort === key ? (dir === "asc" ? "ascending" : "descending") : "none"}>
      <button type="button" onClick={() => sortBy(key)} className="inline-flex items-center gap-1 hover:text-foreground">
        {label}
        {sort === key && (dir === "asc" ? <ArrowUp className="w-3 h-3" aria-hidden /> : <ArrowDown className="w-3 h-3" aria-hidden />)}
      </button>
    </th>
  );

  const total = data ? Object.values(data.state_counts).reduce((a, b) => a + b, 0) : 0;

  return (
    <div className="space-y-6">
      <div><LinkedInHubTabs /></div>
      <PageHeader
        title="LinkedIn Outreach"
        description={data ? `Every LinkedIn path across all RFPs · ${data.total_count} ${data.total_count === 1 ? "path" : "paths"}${filtered ? " match" : ""}` : "Loading…"}
      />
      {error && <Alert tone="danger" role="alert">{error}</Alert>}

      <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Filter by state">
        <FilterChip active={state === ""} onClick={() => narrow("state")("")} count={total}>All</FilterChip>
        {PATH_STATES.map((s) => (
          <FilterChip key={s} active={state === s} onClick={() => narrow("state")(state === s ? "" : s)} count={data?.state_counts[s] ?? 0}>{STATE_LABEL[s]}</FilterChip>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <input type="search" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Search company, person or RFP" aria-label="Search the pipeline"
            className={`pl-8 pr-3 py-1.5 w-64 ${FORM_CONTROL}`} />
        </div>
        <select value={rfp} onChange={(e) => narrow("rfp")(e.target.value)} className={`px-3 py-1.5 ${FORM_CONTROL}`} aria-label="Filter by RFP">
          <option value="">All RFPs</option>
          {(data?.rfps ?? []).map((r) => <option key={r.id} value={r.id}>{r.title ?? `RFP ${r.id}`}</option>)}
        </select>
        <select value={actor} onChange={(e) => narrow("by")(e.target.value)} className={`px-3 py-1.5 ${FORM_CONTROL}`} aria-label="Last action by">
          <option value="">Anyone</option>
          {(data?.actors ?? []).map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          Active from
          <input type="date" value={from} max={to || undefined} onChange={(e) => narrow("from")(e.target.value)} className={`px-2 py-1.5 ${FORM_CONTROL}`} aria-label="Active from" />
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          to
          <input type="date" value={to} min={from || undefined} onChange={(e) => narrow("to")(e.target.value)} className={`px-2 py-1.5 ${FORM_CONTROL}`} aria-label="Active to" />
        </label>
        {filtered && <Button variant="ghost" size="sm" onClick={reset}>Clear filters</Button>}
      </div>

      <div className={`${TABLE_SHELL} overflow-hidden ${loading && data ? "opacity-60 transition-opacity" : ""}`} aria-busy={loading}>
        <table className="w-full text-sm">
          <thead>
            <tr className={THEAD_ROW}>
              {sortHead("company", "Company and contact")}
              {sortHead("rfp", "RFP")}
              <th className={TH}>Progress</th>
              {sortHead("days_in_state", "Status")}
              {sortHead("last_action", "Last activity")}
              <th className={TH}>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {data && data.items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">{filtered ? "No path matches these filters." : "No LinkedIn path has been planned yet."}</td></tr>
            )}
            {!data && !error && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-muted-foreground">Loading…</td></tr>
            )}
            {(data?.items ?? []).map((p) => (
              <tr key={p.key} className={TBODY_ROW} data-testid={`path-${p.key}`}>
                <td className={`${TD} min-w-[14rem]`}>
                  <button type="button" onClick={() => setOpen(p)} className="font-semibold text-foreground text-left hover:text-primary hover:underline underline-offset-2">{p.company_name ?? "Company"}</button>
                  <p className="text-xs text-muted-foreground">
                    {[p.contact_name, p.contact_title].filter(Boolean).join(" · ") || p.contact_email || "No contact chosen"}
                    {p.profile_url && <> · <a href={p.profile_url} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">LinkedIn</a></>}
                  </p>
                </td>
                <td className={`${TD} max-w-[14rem]`}>
                  <Link className="text-muted-foreground underline underline-offset-2 line-clamp-2" to={`${crmBase()}?opportunity_id=${p.opportunity_id}`}>{p.rfp_title ?? "RFP"}</Link>
                </td>
                <td className={TD}><PathStepper path={p} /></td>
                <td className={`${TD} min-w-[10rem]`}>
                  <StatusPill tone={STATE_TONE[p.state]} size="sm">{STATE_LABEL[p.state]}</StatusPill>
                  {daysText(p) && <p className="mt-1 text-xs text-muted-foreground">{daysText(p)}</p>}
                  {p.reason && (p.state === "closed" || p.state === "skipped") && <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2" title={p.reason}>{p.reason}</p>}
                </td>
                <td className={`${TD} min-w-[9rem]`}>
                  {p.last_activity_on ? (
                    <>
                      <p className="text-sm">{formatCalendarDate(p.last_activity_on)}</p>
                      <p className="text-xs text-muted-foreground">{daysAgo(p.last_activity_on, data?.today)}{p.last_action && ` · ${ACTION_LABEL[p.last_action.action] ?? p.last_action.action} by ${p.last_action.actor_label}`}</p>
                    </>
                  ) : <span className="text-muted-foreground">—</span>}
                </td>
                <td className={TD}>
                  {p.conversation?.outcome
                    ? <StatusPill tone={resultStatus(p.conversation.outcome)!.tone} size="sm">{resultStatus(p.conversation.outcome)!.label}</StatusPill>
                    : p.conversation ? <StatusPill tone="info" size="sm">{p.conversation.open ? "open" : "closed"}</StatusPill> : <span className="text-muted-foreground">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total_pages > 1 && <PagerNav page={data.page} totalPages={data.total_pages} onChange={setPage} />}
      {open && <PathDetailModal path={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
